// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { EditablePropertyButton } from '@/components/editable-property-button';
import { SupportSnackbar } from '@/components/support-snackbar';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Project } from '@/project/api/types';
import { UpdateProjectParameters } from '@/project/api/update-project';
import {
  ProjectKeyDateField,
  ProjectKeyDateLabel,
} from '@/project/components/project-key-date-label';
import { useUpdateProject } from '@/project/hooks/use-update-project';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Divider, Popover, Stack } from '@mui/material';
import { DateCalendar } from '@mui/x-date-pickers-pro';
import { DateTime } from 'luxon';
import { useRef } from 'react';
import { SubmitHandler, useController, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({ date: dateSchema.nullable() });

type FormData = z.infer<typeof formSchema>;

interface Props {
  project: Project;
  field: ProjectKeyDateField;
}

export function ProjectKeyDateControl(props: Props) {
  const editor = useDisclosure();
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  const { control, formState, reset, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { date: props.project[props.field] },
  });

  const dateController = useController({ control, name: 'date' });

  const updateProjectMutation = useUpdateProject(props.project.key, {
    onSuccess: () => {
      editor.close();
    },
  });

  const shouldUpdate = formState.isDirty;

  const buildPayload = (date: DateTime | null): UpdateProjectParameters => {
    const payload: UpdateProjectParameters = {};
    payload[props.field] = date;
    return payload;
  };

  const close = () => {
    editor.close();
    reset();
  };

  const clearValue = () => {
    close();
    return updateProjectMutation.mutate(buildPayload(null));
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (shouldUpdate) {
      return updateProjectMutation.mutate(buildPayload(data.date));
    }
  };

  return (
    <>
      <SupportSnackbar
        isOpen={updateProjectMutation.isError}
        onClose={updateProjectMutation.reset}
        message="An error occurred while updating the project."
      />
      <Popover
        anchorEl={buttonRef.current}
        open={editor.isOpen}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Stack component="form" onSubmit={handleSubmit(handleValidSubmission)}>
          <DateCalendar
            value={dateController.field.value}
            onChange={dateController.field.onChange}
          />
          <Divider />
          <Stack
            spacing={1}
            useFlexGap
            padding={1}
            direction="row"
            justifyContent="end"
          >
            <Button onClick={clearValue} sx={{ marginRight: 'auto' }}>
              Clear
            </Button>
            <Button onClick={close}>Cancel</Button>
            <Button type="submit" color="secondary" variant="contained">
              Save
            </Button>
          </Stack>
        </Stack>
      </Popover>
      <EditablePropertyButton ref={buttonRef} onClick={editor.open}>
        <ProjectKeyDateLabel
          project={props.project}
          field={props.field}
          variant="body2"
        />
      </EditablePropertyButton>
    </>
  );
}
